import {
  createContext,
  createElement,
  PropsWithChildren,
  useContext,
} from "react";
import { invariant } from "./helpers";

interface Slot<T> {
  read(): T;
}

type State<T> =
  | { status: "pending"; promise: Promise<void> }
  | { status: "error"; error: unknown }
  | { status: "success"; data: T };
class OnceSlot<T> implements Slot<T> {
  #state: State<T>;

  constructor(promise: Promise<T>) {
    this.#state = {
      status: "pending",
      promise: promise.then(
        (data) => {
          this.#state = { status: "success", data };
        },
        (error) => {
          this.#state = { status: "error", error };
        },
      ),
    };
  }

  read(): T {
    switch (this.#state.status) {
      case "pending":
        throw this.#state.promise;
      case "success":
        return this.#state.data;
      case "error":
        throw this.#state.error;
    }
  }
}

const Context = createContext<{ slot: Slot<unknown> | null } | null>(null);

const SlotContext = (props: PropsWithChildren) => {
  return createElement(Context.Provider, { ...props, value: { slot: null } });
};

const useOnceSlot = <T>(request: () => Promise<T>): T => {
  const context = useContext(Context);
  invariant(!!context, "SlotContext is required");
  if (!context.slot) {
    context.slot = new OnceSlot(request());
  }
  return context.slot.read() as T;
};

export { SlotContext, useOnceSlot };
